import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { supabase } from "@/lib/supabaseClient";
import {
  listBubbleSupportMessages,
  markSupportMessageSoundPlayed,
  sendBubbleSupportMessage,
  uploadSupportAttachment,
} from "@/lib/supportApi";
import { playSupportNotificationSound } from "@/lib/supportNotificationSound";
import SupportComposer from "@/components/support/SupportComposer";
import SupportThread from "@/components/support/SupportThread";

const FOUNDING_WELCOME_KEY = "founding_owner_welcome";
const SUPPORT_WELCOME_STORAGE_PREFIX = "dental-os:support-welcome";
const SUPPORT_SLOT_WIDTH = 56;
const SUPPORT_SLOT_HEIGHT = 56;
const SUPPORT_SLOT_RIGHT = 20;
const SUPPORT_SLOT_BOTTOM = 44;
const SUPPORT_COLLISION_SELECTOR = [
  "button",
  "a[href]",
  "input",
  "select",
  "textarea",
  "[role='button']",
  "[role='dialog']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export default function SupportChatBubble() {
  const { user, activeClinic } = useAuth();
  const { moduleLoading, canAccessModule } = useFeatures();
  const allowed = !moduleLoading && canAccessModule("support");
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [collisionHidden, setCollisionHidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [error, setError] = useState("");
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const handledSoundIdsRef = useRef(new Set());

  const refreshCollisionState = useCallback(() => {
    if (!allowed || open) {
      setCollisionHidden(false);
      return;
    }
    setCollisionHidden(hasBottomRightInteractiveCollision(rootRef.current));
  }, [allowed, open]);

  useEffect(() => {
    if (!allowed) return undefined;
    refreshCollisionState();

    const onLayoutChange = () => {
      refreshCollisionState();
    };
    window.addEventListener("resize", onLayoutChange);
    window.addEventListener("scroll", onLayoutChange, true);

    const observer = typeof MutationObserver === "function"
      ? new MutationObserver(onLayoutChange)
      : null;
    observer?.observe(document.body, {
      attributes: true,
      attributeFilter: ["aria-hidden", "class", "hidden", "style"],
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("resize", onLayoutChange);
      window.removeEventListener("scroll", onLayoutChange, true);
      observer?.disconnect();
    };
  }, [allowed, refreshCollisionState]);

  useEffect(() => {
    if (!allowed || open) return undefined;
    let cancelled = false;
    setError("");
    listBubbleSupportMessages({ markRead: false })
      .then((payload) => {
        if (cancelled) return;
        setConversation(payload.conversation);
        setMessages(payload.messages);
        setUnread(payload.unread_count || 0);
        handlePendingSound(payload.pending_sound);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError?.message || "Could not load support.");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, activeClinic?.clinic_id, open]);

  useEffect(() => {
    if (!allowed || !open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    listBubbleSupportMessages({ markRead: true })
      .then((payload) => {
        if (cancelled) return;
        setConversation(payload.conversation);
        setMessages(payload.messages);
        setUnread(0);
        handlePendingSound(payload.pending_sound);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError?.message || "Could not load support.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [allowed, open]);

  useEffect(() => {
    if (!allowed || !conversation?.id) return undefined;
    const channel = supabase
      .channel(`support-conversation-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          setMessages((current) => (
            current.some((existing) => existing.id === row.id) ? current : [...current, row]
          ));
          if (!open && row.sender_role === "admin") {
            setUnread((value) => value + 1);
            if (row.notification_sound_key) {
              handlePendingSound({
                message_id: row.id,
                sound_key: row.notification_sound_key,
              });
            }
            if (row.system_key === FOUNDING_WELCOME_KEY) {
              setUnread((value) => Math.max(value, 1));
            }
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, conversation?.id, open]);

  useEffect(() => {
    if (!open) return;
    const node = scrollRef.current;
    if (!node) return;
    if (isFoundingWelcomeOnly(messages)) {
      node.scrollTop = 0;
      return;
    }
    if (typeof node.scrollTo === "function") {
      node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
    } else {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, open]);

  if (!allowed) return null;

  async function handleAttach(file) {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadSupportAttachment({
        file,
        clinicId: activeClinic?.clinic_id,
        conversationId: conversation?.id || "bubble",
      });
      setAttachment(uploaded);
    } catch (attachError) {
      setError(attachError?.message || "Could not upload attachment.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSend(body) {
    if (!body && !attachment) return;
    setSending(true);
    setError("");
    try {
      const payload = await sendBubbleSupportMessage({ body, attachment });
      setConversation(payload.conversation);
      setMessages(payload.messages);
      setAttachment(null);
    } catch (sendError) {
      setError(sendError?.message || "Could not send this right now.");
      toast.error("Could not send support message.");
    } finally {
      setSending(false);
    }
  }

  function collapseSupportChat() {
    const welcomeMessageId = getWelcomeMessageId(messages);
    if (welcomeMessageId) {
      rememberWelcomeAutoOpen({
        userId: user?.id,
        clinicId: activeClinic?.clinic_id,
        messageId: welcomeMessageId,
      });
    }
    setOpen(false);
  }

  async function handlePendingSound(pendingSound) {
    const messageId = pendingSound?.message_id;
    if (!messageId || handledSoundIdsRef.current.has(messageId)) return;
    handledSoundIdsRef.current.add(messageId);

    const played = await playSupportNotificationSound({
      messageId,
      soundKey: pendingSound.sound_key,
    });
    if (!played) return;

    try {
      await markSupportMessageSoundPlayed(messageId);
    } catch {
      handledSoundIdsRef.current.delete(messageId);
    }
  }

  return (
    <div
      ref={rootRef}
      className="fixed bottom-11 right-5 z-50 sm:bottom-12 sm:right-7"
      data-testid="support-chat-root"
    >
      {collisionHidden && !open ? null : open ? (
        <section
          data-testid="support-chat-panel"
          className="mb-3 flex w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20"
          style={{
            height: "min(680px, calc(100dvh - 4rem))",
            maxHeight: "calc(100dvh - 4rem)",
          }}
        >
          <header className="flex items-start justify-between gap-3 border-b border-slate-200 bg-teal-700 px-4 py-3 text-white">
            <div>
              <h2 className="text-base font-semibold">Support</h2>
              <p className="text-xs text-teal-50/85">We usually reply in a few hours</p>
            </div>
            <button
              type="button"
              onClick={collapseSupportChat}
              className="rounded-lg p-1.5 text-white/85 hover:bg-white/10 hover:text-white"
              aria-label="Collapse support"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          </header>
          <div ref={scrollRef} data-testid="support-chat-scroll" className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">Loading support...</div>
            ) : (
              <SupportThread messages={messages} greeting compact />
            )}
          </div>
          <SupportComposer
            placeholder="Message..."
            disabled={loading}
            sending={sending}
            uploading={uploading}
            error={error}
            attachment={attachment}
            onAttach={handleAttach}
            onClearAttachment={() => setAttachment(null)}
            onSend={handleSend}
          />
        </section>
      ) : null}
      {collisionHidden && !open ? null : (
        <button
          type="button"
          data-testid="support-chat-fab"
          onClick={() => {
            setOpen(true);
            setUnread(0);
          }}
          className={[
            "group relative inline-flex h-10 w-10 items-center justify-center rounded-full",
            "border border-white/70 bg-slate-50/55 text-slate-700",
            "shadow-[0_12px_28px_-8px_rgba(15,23,42,0.28),0_2px_6px_-2px_rgba(15,23,42,0.10),inset_0_1px_1px_rgba(255,255,255,1),inset_0_0_0_0.5px_rgba(255,255,255,0.5),inset_0_-2px_6px_-2px_rgba(15,23,42,0.14),inset_0_0_10px_rgba(255,255,255,0.18)]",
            "backdrop-blur-xl backdrop-saturate-[1.8] backdrop-brightness-[1.05]",
            "transition-all duration-300 ease-out",
            "hover:-translate-y-0.5 hover:border-teal-500/70 hover:bg-teal-600 hover:text-white",
            "hover:shadow-[0_14px_30px_-6px_rgba(13,148,136,0.32),inset_0_1px_0_rgba(255,255,255,0.4)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2",
          ].join(" ")}
          aria-label="Open support chat"
        >
          <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/55 via-white/0 to-white/18 opacity-95 transition-opacity duration-300 group-hover:opacity-0" />
          <span className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(50%_35%_at_30%_22%,rgba(255,255,255,1)_0%,rgba(255,255,255,0.25)_45%,rgba(255,255,255,0)_75%)] opacity-95 transition-opacity duration-300 group-hover:opacity-15" />
          <MessageCircle className="relative h-[18px] w-[18px]" strokeWidth={2} />
          {unread > 0 ? (
            <span
              data-testid="support-chat-unread-badge"
              className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 py-0.5 text-[10px] font-bold text-white shadow-sm"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          ) : null}
        </button>
      )}
    </div>
  );
}

function hasBottomRightInteractiveCollision(root) {
  const slot = getSupportSlotRect();
  if (document.elementsFromPoint) {
    const points = [
      [slot.left + slot.width / 2, slot.top + slot.height / 2],
      [slot.left + 6, slot.top + 6],
      [slot.right - 6, slot.bottom - 6],
    ];
    return points.some(([x, y]) => (
      document.elementsFromPoint(x, y).some((element) => isBlockingElement(element, root))
    ));
  }

  return Array.from(document.querySelectorAll(SUPPORT_COLLISION_SELECTOR))
    .some((element) => {
      if (!isBlockingElement(element, root)) return false;
      return rectsIntersect(slot, element.getBoundingClientRect());
    });
}

function getSupportSlotRect() {
  const width = SUPPORT_SLOT_WIDTH;
  const height = SUPPORT_SLOT_HEIGHT;
  const right = window.innerWidth - SUPPORT_SLOT_RIGHT;
  const bottom = window.innerHeight - SUPPORT_SLOT_BOTTOM;
  return {
    left: right - width,
    top: bottom - height,
    right,
    bottom,
    width,
    height,
  };
}

function isBlockingElement(element, root) {
  if (!element || element === document.body || element === document.documentElement) return false;
  if (root && root.contains(element)) return false;
  if (!element.matches?.(SUPPORT_COLLISION_SELECTOR)) return false;
  if (element.disabled || element.getAttribute("aria-hidden") === "true") return false;

  const rect = element.getBoundingClientRect();
  if (!rect || rect.width <= 0 || rect.height <= 0) return false;

  const style = window.getComputedStyle?.(element);
  if (style?.display === "none" || style?.visibility === "hidden" || style?.pointerEvents === "none") {
    return false;
  }

  return true;
}

function rectsIntersect(first, second) {
  return first.left < second.right
    && first.right > second.left
    && first.top < second.bottom
    && first.bottom > second.top;
}

function isFoundingWelcomeOnly(messages) {
  return messages.length === 1 && messages[0]?.system_key === FOUNDING_WELCOME_KEY;
}

function getWelcomeMessageId(messages = []) {
  return messages.find((message) => message.system_key === FOUNDING_WELCOME_KEY)?.id || null;
}

function getWelcomeStorageKey(userId, clinicId) {
  if (!userId || !clinicId) return null;
  return `${SUPPORT_WELCOME_STORAGE_PREFIX}:${userId}:${clinicId}`;
}

function rememberWelcomeAutoOpen({ userId, clinicId, messageId }) {
  const key = getWelcomeStorageKey(userId, clinicId);
  if (!key || !messageId) return;
  try {
    window.localStorage.setItem(key, messageId);
  } catch {
    // Private browsing or storage policy failures should not re-open the popup in this session.
  }
}
